(() => {
  if (customElements.get("diana-react-island")) return;

  const FIXED_ENTRIES = new Set([
    "diana-sky-island.js",
    "diana-story-island.js",
  ]);
  const IMPORT_RETRY_DELAY_MS = 250;

  const isShopifyHost = (hostname) =>
    /(?:^|\.)(?:shopify\.com|myshopify\.com|shopifycdn\.com)$/i.test(hostname);

  const validatedEntry = (value) => {
    if (typeof value !== "string" || value === "" || /\s|[\u0000-\u001f\u007f]|\\/.test(value)) {
      throw new TypeError("Invalid island entry URL");
    }
    if (!value.startsWith("/") && !/^https:\/\//i.test(value)) {
      throw new TypeError("Invalid island entry URL");
    }
    const entryUrl = new URL(value, window.location.href);
    if (entryUrl.protocol !== "https:" || entryUrl.username || entryUrl.password || entryUrl.hash) {
      throw new TypeError("Invalid island entry URL");
    }
    if (entryUrl.origin !== window.location.origin && !isShopifyHost(entryUrl.hostname)) {
      throw new TypeError("Invalid island entry host");
    }
    const fileName = entryUrl.pathname.split("/").pop();
    if (!entryUrl.pathname.includes("/assets/") || !FIXED_ENTRIES.has(fileName)) {
      throw new TypeError("Unknown island entry asset");
    }
    return value;
  };

  const retryEntry = (entry, attempt) => {
    const entryUrl = new URL(entry, window.location.href);
    entryUrl.searchParams.set("diana_retry", String(attempt));
    return entryUrl.href;
  };

  const wait = (delay) => new Promise((resolve) => window.setTimeout(resolve, delay));

  class DianaReactIsland extends HTMLElement {
    static importer = (entry) => import(entry);

    #cleanup = null;
    #connected = false;
    #generation = 0;

    connectedCallback() {
      if (this.#connected) return;
      this.#connected = true;
      const generation = ++this.#generation;
      this.#showFallback("loading");
      if (!this.hasAttribute("data-entry")) return;
      void this.#enhance(generation);
    }

    disconnectedCallback() {
      this.#connected = false;
      this.#generation += 1;
      this.#showFallback("loading");
      this.#cleanupOnce();
    }

    async #enhance(generation) {
      let phase = "parse";
      try {
        const propsNode = this.querySelector("[data-diana-island-props]");
        const entry = validatedEntry(this.dataset.entry);
        if (!propsNode) throw new TypeError("Missing island props");
        const props = JSON.parse(propsNode.textContent || "");

        phase = "import";
        const islandModule = await this.#importWithRetry(entry, generation);
        if (!this.#isCurrent(generation)) return;
        if (!islandModule || typeof islandModule.mount !== "function") {
          throw new TypeError("Island entry does not export mount");
        }

        phase = "mount";
        const cleanup = await islandModule.mount(this, props);
        if (typeof cleanup !== "function") {
          throw new TypeError("Island mount did not return cleanup");
        }
        if (!this.#isCurrent(generation)) {
          cleanup();
          return;
        }

        this.#cleanup = cleanup;
        this.dataset.enhanced = "true";
        this.dataset.islandState = "ready";
        const fallback = this.querySelector("[data-diana-island-fallback]");
        if (fallback) fallback.hidden = true;
      } catch (_error) {
        if (!this.#isCurrent(generation)) return;
        this.#showFallback("failed");
        this.dispatchEvent(new CustomEvent("diana:island-error", {
          bubbles: true,
          detail: {
            island: this.dataset.dianaIsland || "unknown",
            sectionId: this.dataset.sectionId || "unknown",
            phase,
          },
        }));
      }
    }

    async #importWithRetry(entry, generation) {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const candidate = attempt === 0 ? entry : retryEntry(entry, attempt);
          return await this.constructor.importer(candidate);
        } catch (error) {
          if (attempt === 1) throw error;
          if (!this.#isCurrent(generation)) return null;
          this.dataset.islandState = "retrying";
          await wait(IMPORT_RETRY_DELAY_MS);
          if (!this.#isCurrent(generation)) return null;
        }
      }
      return null;
    }

    #isCurrent(generation) {
      return this.#connected && this.isConnected && this.#generation === generation;
    }

    #showFallback(state) {
      this.removeAttribute("data-enhanced");
      this.dataset.islandState = state;
      const fallback = this.querySelector("[data-diana-island-fallback]");
      if (fallback) fallback.hidden = false;
      const loading = this.querySelector("[data-diana-island-loading]");
      if (loading) loading.hidden = state === "failed";
      const failure = this.querySelector("[data-diana-island-failure]");
      if (failure) failure.hidden = state !== "failed";
    }

    #cleanupOnce() {
      const cleanup = this.#cleanup;
      this.#cleanup = null;
      if (cleanup) cleanup();
    }
  }

  customElements.define("diana-react-island", DianaReactIsland);
})();
